'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["aria-labelledby", "children", "className", "description", "disabled", "error", "Fieldset", "gap", "help", "inline", "label", "required", "tooltip", "tooltipPlacement", "use", "validationMessage"];
import PropTypes from 'prop-types';
import { cloneElement, Component, Children } from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { DISTANCE_MEASUREMENT_EXTRA_SMALL, MERCURY_LAYER, VENUS_LAYER } from 'HubStyleTokens/sizes';
import getComponentName from 'react-utils/getComponentName';
import devLogger from 'react-utils/devLogger';
import UIFieldset from './UIFieldset';
import UIFormControl from './UIFormControl';
import Small from '../elements/Small';
import { hidden } from '../utils/propTypes/decorators';
import { getComponentPropType } from '../utils/propTypes/componentProp';
import { uniqueId } from '../utils/underscore';
import { FONT_FAMILIES } from '../utils/Styles';
import deprecated from '../utils/propTypes/deprecated';
import { warnIfFragment } from '../utils/devWarnings';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const descriptionWarningMessage = () => ({
  message: `UIInputGroup: the \`description\` prop has been deprecated. Please use the \`help\` prop instead.`,
  key: `UIInputGroup: description prop`
});
const getLengthValue = length => {
  let computedLength;
  if (typeof length === 'boolean') {
    computedLength = length ? DISTANCE_MEASUREMENT_EXTRA_SMALL : false;
  }
  if (typeof length === 'string') {
    computedLength = length;
  }
  if (typeof length === 'number') {
    computedLength = `${parseInt(length, 10)}px`;
  }
  return computedLength;
};
const formGroupStartStyles = css([".private-form__control{border-bottom-right-radius:0;border-top-right-radius:0;}"]);
const formGroupEndStyles = css([".private-form__control{border-top-left-radius:0;border-bottom-left-radius:0;}"]);
const formGroupMemberMediaBodyStyles = css(["flex:1 1 0%;min-width:0;"]);
const InputGroupImpl = styled.div.withConfig({
  displayName: "UIInputGroup__InputGroupImpl",
  componentId: "sc-10l4osz-0"
})(["display:", ";> *{.private-form__control{&.private-form__control--error{position:relative;z-index:", ";}&:focus{position:relative;z-index:", ";}}", ";", ";}>:not(:first-child){", ";", ";}>:not(:last-child){", ";", ";}.private-form__set + .private-form__set{margin-top:0;margin-left:", ";}"], ({
  inline
}) => inline ? 'inline-flex' : 'flex', MERCURY_LAYER, VENUS_LAYER, props => props.use === 'itemBoth' && css([":not(:first-child):not(:last-child){", ";}"], formGroupMemberMediaBodyStyles), props => props.use === 'flex' && formGroupMemberMediaBodyStyles, props => !getLengthValue(props.gap) && formGroupEndStyles, props => props.use === 'itemLeft' && formGroupMemberMediaBodyStyles, props => !getLengthValue(props.gap) && formGroupStartStyles, props => props.use === 'itemRight' && formGroupMemberMediaBodyStyles, props => !getLengthValue(props.gap) ? `-1px` : props.gap);
const InputGroupLegend = styled(UIFieldset.defaultProps.Legend).withConfig({
  displayName: "UIInputGroup__InputGroupLegend",
  componentId: "sc-10l4osz-1"
})(["", ";"], FONT_FAMILIES.medium);
export default class UIInputGroup extends Component {
  constructor(props) {
    super(props);
    this._legendId = `input-group-${uniqueId()}`;
  }
  render() {
    const _this$props = this.props,
      {
        'aria-labelledby': ariaLabelledBy,
        children,
        className,
        description,
        disabled,
        error,
        Fieldset,
        gap,
        help,
        inline,
        label: legend,
        required,
        tooltip,
        tooltipPlacement,
        use,
        validationMessage
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const computedClassName = classNames("private-form__set private-input-group", className);

    // Render child UIFormControl's `validationMessage` prop
    const formControlWithError = Children.toArray(children).find(child => child && child.props && child.props.error && child.props.validationMessage);
    const computedError = error || formControlWithError && formControlWithError.props.error;
    const computedValidationMessage = validationMessage || formControlWithError && formControlWithError.props.validationMessage;
    return /*#__PURE__*/_jsxs(Fieldset, Object.assign({}, rest, {
      className: computedClassName,
      disabled: disabled,
      help: help,
      Legend: InputGroupLegend,
      legend: legend,
      legendId: this._legendId,
      tabIndex: 0,
      tooltip: tooltip,
      tooltipPlacement: tooltipPlacement,
      _required: required,
      children: [/*#__PURE__*/_jsx(InputGroupImpl, {
        gap: getLengthValue(gap),
        inline: inline,
        use: use,
        children: Children.map(children, child => {
          if (child == null) {
            return null;
          }
          warnIfFragment(child, UIInputGroup.displayName);
          const ariaLabel = child.props['aria-label'];
          const labelledBy = ariaLabelledBy || child.props['aria-labelledby'];
          if (process.env.NODE_ENV !== 'production' && getComponentName(child) === UIFormControl.displayName && !ariaLabel && !labelledBy) {
            devLogger.warn({
              message: 'UIInputGroup: You must add an `aria-label` to each `UIFormControl` to describe the specific field in this input group. Alternatively, set `UIFormControl[aria-labelledby]` to the ID of an existing label element.',
              key: 'UIInputGroup: aria-label',
              url: 'https://tools.hubteamqa.com/ui-library/show/UIComponents/form/UIInputGroup#uiinputgroup/basic'
            });
          }
          return /*#__PURE__*/cloneElement(child, {
            'aria-labelledby': ariaLabel ? '' : classNames(legend ? this._legendId : labelledBy, child.props['aria-labelledby']),
            required: child.props.required || required,
            // These props are rendered by the inputgroup instead of the formcontrol
            description: null,
            help: null,
            tooltip: null,
            validationMessage: null
          });
        })
      }), /*#__PURE__*/_jsx("div", {
        className: "private-inputgroup__meta private-form__meta",
        children: /*#__PURE__*/_jsx("div", {
          className: "private-form__messages",
          "aria-hidden": "true",
          children: computedError && computedValidationMessage && /*#__PURE__*/_jsx(Small, {
            className: "private-form__validation",
            tabIndex: -1,
            use: "error",
            children: computedValidationMessage
          })
        })
      }), description && /*#__PURE__*/_jsx(Small, {
        className: "private-form__description",
        tabIndex: -1,
        use: "help",
        children: description
      })]
    }));
  }
}
UIInputGroup.propTypes = {
  'aria-labelledby': PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  description: deprecated(PropTypes.node, descriptionWarningMessage),
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  Fieldset: getComponentPropType(UIFieldset).isRequired,
  help: UIFormControl.propTypes.help,
  gap: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string]),
  inline: hidden(PropTypes.bool),
  label: PropTypes.node,
  required: PropTypes.bool,
  tooltip: UIFormControl.propTypes.tooltip,
  tooltipPlacement: UIFormControl.propTypes.tooltipPlacement,
  use: PropTypes.oneOf(['flex', 'itemLeft', 'itemRight', 'itemBoth', 'none']),
  validationMessage: PropTypes.node
};
UIInputGroup.defaultProps = {
  error: false,
  Fieldset: UIFieldset,
  gap: false,
  inline: false,
  tooltipPlacement: 'top',
  use: 'flex'
};
UIInputGroup.displayName = 'UIInputGroup';